import { Injectable } from '@angular/core';
import { environment } from '../environment/environment';
import { isHighRiskEnabledForClient } from './high-risk-board/high-risk-entitlement';
import { isDischargePlanEnabledForClient } from './discharge-plan/discharge-plan-entitlement';
import { canViewKpis } from '../kpi/kpi-access';
import { localStorageGet } from '../utils';
import { GATEWAY_REFERRAL_LINKS_DISABLED_KEY } from './client-settings.types';
import {
  NavCtx, buildLandingScreens, defaultPathForRole, isLandingScreenAllowed, screensForRole,
} from '../navigation';

export interface AppScreen {
  title: string;
  path: string; // empty for non-navigation entries (e.g. Log Out)
  state?: { [k: string]: any };
}

/**
 * Thin adapter over `src/app/navigation` — the framework-free model shared with the new nav
 * shell (and copied into the federation lib later). Keep the public API stable: the auth
 * service and the profile default-screen picker both call it.
 */
@Injectable({ providedIn: 'root' })
export class NavigationScreensService {
  // Do NOT inject UtilsService here: it would close an ES-module cycle
  // (auth -> nav -> utils -> auth) that breaks NgModule metadata. Mirror the flag locally.
  private isPageNewVersionGloballyEnabled(_pageKey: string): boolean {
    return true;
  }

  // Role -> its post-login landing page. Single source of truth: CustomAuthServiceService
  // redirects through this table (it imports this service; the reverse would close an
  // ES-module cycle), and the profile dropdown marks "(default)" from it.
  static defaultPathForRole(roleValue: number): string {
    return defaultPathForRole(roleValue);
  }

  // Public so NavStateService resolves the new nav through the same ctx the legacy menu uses —
  // two builders would silently drift. Un-stub `isPageNewVersionGloballyEnabled` (and the
  // uiSettings read at utils.service.ts:141) for both surfaces at once.
  navCtx(): NavCtx {
    return {
      isCcare:
        environment.snfClient === environment.CLIENTS_NAMES.CCARE ||
        environment.snfClient === environment.CLIENTS_NAMES.CCARECT,
      referralResponseReportEnabled: this.isPageNewVersionGloballyEnabled('referralResponseReport'),
      // Same client gate the bed board button and HighRiskRouteGuard read, so the
      // rail can never offer a screen the board hides or the guard bounces.
      highRiskEnabled: isHighRiskEnabledForClient(),
      // Same client gate DischargePlanRouteGuard and the bed board's env read,
      // so neither menu can offer a screen the guard bounces.
      dischargePlanEnabled: isDischargePlanEnabledForClient(),
      gatewayReferralLinksDisabled: localStorageGet(GATEWAY_REFERRAL_LINKS_DISABLED_KEY) === true,
      // The same rule KpiRouteGuard applies, so the menu never offers a KPI page the guard bounces.
      canViewKpis,
    };
  }

  getScreensForRole(role: number): AppScreen[] {
    return screensForRole(role, this.navCtx());
  }

  getLandingScreensForRole(role: number): AppScreen[] {
    return buildLandingScreens(role, this.navCtx());
  }

  isLandingScreenAllowed(role: number, path: string): boolean {
    return isLandingScreenAllowed(role, path, this.navCtx());
  }
}
